import { Banner, Icon, Modal, spacing, Text, TextArea, useToast } from '@scality/core-ui';
import { Box, Button } from '@scality/core-ui/dist/next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useChat } from '../../hooks/useChat';

const DISLIKE_CATEGORIES = [
  'Incorrect or incomplete',
  'Not what I asked for',
  'Slow and buggy',
  'Style or tone',
  'Safety or legal concern',
  'Other',
] as const;

type DislikeCategory = (typeof DISLIKE_CATEGORIES)[number];

const FeedbackTextArea = styled(TextArea)`
  width: 100%;
`;

// Width cap so the disclaimer text doesn't drive the modal width and the button grid does it instead.
const DisclaimerText = styled(Text)`
  max-width: 500px;
`;

const FeedbackModalFooter: React.FC<{
  onCancel: () => void;
  onSubmit: () => void;
  isPending: boolean;
  hasError: boolean;
  canSubmit: boolean;
}> = ({ onCancel, onSubmit, isPending, hasError, canSubmit }) => (
  <Box display="flex" justifyContent="end" gap={spacing.r8}>
    <Button variant="outline" onClick={onCancel} label="Cancel" disabled={isPending} />
    <Button variant="primary" onClick={onSubmit} label={hasError ? 'Retry' : 'Submit'} isLoading={isPending} disabled={!canSubmit} />
  </Box>
);

export const FeedbackModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (categories: string[], message: string) => void;
  isPending: boolean;
  hasError: boolean;
}> = ({ isOpen, onClose, onSubmit, isPending, hasError }) => {
  const [selectedCategory, setSelectedCategory] = useState<DislikeCategory | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const handleSubmit = useCallback(() => {
    onSubmit(selectedCategory ? [selectedCategory] : [], feedbackMessage);
  }, [selectedCategory, feedbackMessage, onSubmit]);

  const handleClose = useCallback(() => {
    setSelectedCategory(null);
    setFeedbackMessage('');
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      title={<>Send feedback</>}
      wide
      footer={
        <FeedbackModalFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          isPending={isPending}
          hasError={hasError}
          canSubmit={selectedCategory !== null}
        />
      }
    >
      <Box display="flex" flexDirection="column" gap={spacing.r24} width="100%">
        <Box display="flex" flexDirection="column" gap={spacing.r8}>
          <Text color="textSecondary">Tell us what you dislike in this reply?</Text>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={spacing.r8} role="radiogroup" aria-label="Feedback categories">
            {DISLIKE_CATEGORIES.map((category) => (
              <Button
                key={category}
                label={category}
                variant={selectedCategory === category ? 'primary' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                style={{ width: '100%' }}
                role="radio"
                aria-checked={selectedCategory === category}
              />
            ))}
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={spacing.r8}>
          <Text color="textSecondary">Want to tell us more? (optional)</Text>
          <FeedbackTextArea
            value={feedbackMessage}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFeedbackMessage(e.target.value)
            }
            placeholder="Example: It would be more helpful if ..."
            name="feedbackMessage"
            rows={3}
          />
        </Box>

        <DisclaimerText variant="Small" color="textSecondary">
          This conversation will be included with your feedback to help improve our product quality. It won&apos;t be used in AI model training.
        </DisclaimerText>
        {hasError && (
          <Banner variant="danger" withDefaultIcon>
            Failed to submit feedback. Try again.
          </Banner>
        )}
      </Box>
    </Modal>
  );
};

type FeedbackSentiment = 'up' | 'down' | null;

export const FeedbackButtons: React.FC<{ messageTimestamp: string }> = ({ messageTimestamp }) => {
  const { feedback } = useChat();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasModalError, setHasModalError] = useState(false);

  const feedbackQueryKey = useMemo(() => ['chatFeedback', messageTimestamp] as const, [messageTimestamp]);
  const { data: submittedSentiment = null } = useQuery<FeedbackSentiment>({
    queryKey: feedbackQueryKey,
    initialData: null,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { shouldPersist: true },
  });

  const setSentiment = useCallback(
    (sentiment: FeedbackSentiment) => {
      queryClient.setQueryData<FeedbackSentiment>(feedbackQueryKey, sentiment);
    },
    [queryClient, feedbackQueryKey],
  );

  const handleThumbsUp = useCallback(() => {
    feedback.submit(
      { isPositiveFeedback: true, categories: [], feedbackMessage: '' },
      {
        onSuccess: () => setSentiment('up'),
        onError: () => {
          toast.showToast({
            status: 'error',
            autoDismiss: true,
            open: true,
            message: 'Failed to submit feedback. Try again.',
          });
        },
      },
    );
  }, [feedback.submit, setSentiment, toast]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setHasModalError(false);
  }, []);

  const handleModalSubmit = useCallback(
    (categories: string[], feedbackMessage: string) => {
      setHasModalError(false);
      feedback.submit(
        { isPositiveFeedback: false, categories, feedbackMessage },
        {
          onSuccess: () => {
            setSentiment('down');
            setIsModalOpen(false);
          },
          onError: () => {
            setHasModalError(true);
          },
        },
      );
    },
    [feedback.submit, setSentiment],
  );

  const isAlreadyRated = submittedSentiment !== null;

  return (
    <>
      <Button
        icon={<Icon name="ThumbsUp" />}
        tooltip={{ overlay: 'Good response' }}
        onClick={handleThumbsUp}
        disabled={isAlreadyRated || feedback.isSubmitting}
        variant={submittedSentiment === 'up' ? 'primary' : undefined}
        aria-pressed={submittedSentiment === 'up'}
      />
      <Button
        icon={<Icon name="ThumbsDown" />}
        tooltip={{ overlay: 'Bad response' }}
        onClick={() => setIsModalOpen(true)}
        disabled={isAlreadyRated || feedback.isSubmitting}
        variant={submittedSentiment === 'down' ? 'primary' : undefined}
        aria-pressed={submittedSentiment === 'down'}
      />

      <FeedbackModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        isPending={feedback.isSubmitting}
        hasError={hasModalError}
      />
    </>
  );
};
